/* eslint-disable */
// @ts-nocheck

declare const __type__: unique symbol;

/**
 * String containing potentially large integer, which cannot be safely coerced to JavaScript number.
 * Use the provided conversion functions to convert the type to regular JavaScript types.
 */
export type BigIntString = string & { [__type__]: `BigIntString` };

/**
 * Converts the BigIntString into a regular JavaScript bigint.
 */
export const bigIntStringToBigInt: (value: BigIntString) => bigint = BigInt;
/**
 * Converts the BigIntString into a regular JavaScript number.
 * Potentially dangerous, use `Number.isSafeInteger()` after conversion.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/isSafeInteger
 */
export const bigIntStringToNumber: (value: BigIntString) => number = Number;
/**
 * Converts the BigIntString into a regular JavaScript string.
 */
export const bigIntStringToString: (value: BigIntString) => string = String;
/**
 * Converts a JavaScript values to BigIntString type.
 */
export const toBigIntString: (
  value: string | bigint | number,
) => BigIntString = (v) => String(BigInt(v)) as BigIntString;

/**
 * String containing binary data encoded as base64, used for protobuf bytes fields.
 * Use the provided conversion functions to convert the type from/to Uint8Array.
 */
export type BytesString = string & { [__type__]: `BytesString` };

// lookup table from base64 character to byte
const encTable =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/".split("");

// lookup table from base64 character *code* to byte because lookup by number is fast
const decTable: Record<number, number> = {};
for (let i = 0; i < encTable.length; i++) {
  decTable[encTable[i].charCodeAt(0)] = i;
}
// support base64url variants
decTable["-".charCodeAt(0)] = encTable.indexOf("+");
decTable["_".charCodeAt(0)] = encTable.indexOf("/");

/**
 * Converts Uint8Array to BytesString. Thanks @timostamm !
 * @see https://github.com/bufbuild/protobuf-es/blob/5893ec6efb7111d7dbc263aeeb75d693426cacdd/packages/protobuf/src/proto-base64.ts#L42
 */
export const toBytesString: (b: Uint8Array) => BytesString =
  typeof Uint8Array.prototype.toBase64 === `function`
    ? // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Uint8Array/toBase64
      (b) => b.toBase64() as BytesString
    : (bytes) => {
        const base64 = [];
        let groupPos = 0, // position in base64 group
          p = 0; // carry over from previous byte

        for (const b of bytes) {
          switch (groupPos) {
            case 0:
              base64.push(encTable[b >> 2]);
              p = (b & 3) << 4;
              groupPos = 1;
              break;
            case 1:
              base64.push(encTable[p | (b >> 4)]);
              p = (b & 15) << 2;
              groupPos = 2;
              break;
            case 2:
              base64.push(encTable[p | (b >> 6)]);
              base64.push(encTable[b & 63]);
              groupPos = 0;
              break;
          }
        }

        // add output padding
        if (groupPos) {
          base64.push(encTable[p]);
          base64.push("=");
          if (groupPos == 1) base64.push("=");
        }

        return base64.join(``) as BytesString;
      };

/**
 * Converts BytesString to Uint8Array.
 */
export const bytesStringToUint8Array: (bs: BytesString) => Uint8Array =
  typeof Uint8Array.fromBase64 === `function`
    ? // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Uint8Array/fromBase64
      (bs) => Uint8Array.fromBase64(bs)
    : // Thanks @timostamm !
      // @see https://github.com/bufbuild/protobuf-es/blob/5893ec6efb7111d7dbc263aeeb75d693426cacdd/packages/protobuf/src/proto-base64.ts#L97
      (base64Str) => {
        // estimate byte size, not accounting for inner padding and whitespace
        let es = (base64Str.length * 3) / 4;
        if (base64Str.endsWith(`==`)) es -= 2;
        else if (base64Str.endsWith(`=`)) es -= 1;

        const bytes = new Uint8Array(es);
        let bytePos = 0, // position in byte array
          groupPos = 0, // position in base64 group
          b, // current byte
          p = 0; // previous byte
        for (let i = 0, l = base64Str.length; i < l; i++) {
          b = decTable[base64Str.charCodeAt(i)];
          if (b === undefined) {
            switch (base64Str[i]) {
              case "=":
                groupPos = 0; // reset state when padding found
              case "\n":
              case "\r":
              case "\t":
              case " ":
                continue; // skip white-space, and padding
              default:
                throw Error("invalid base64 string.");
            }
          }
          switch (groupPos) {
            case 0:
              p = b;
              groupPos = 1;
              break;
            case 1:
              bytes[bytePos++] = (p << 2) | ((b & 48) >> 4);
              p = b;
              groupPos = 2;
              break;
            case 2:
              bytes[bytePos++] = ((p & 15) << 4) | ((b & 60) >> 2);
              p = b;
              groupPos = 3;
              break;
            case 3:
              bytes[bytePos++] = ((p & 3) << 6) | b;
              groupPos = 0;
              break;
          }
        }
        if (groupPos == 1) throw Error("invalid base64 string.");
        return bytes.subarray(0, bytePos);
      };

/**
 * Retrieves property from an object with path specified by dot notation.
 */
export const get = (obj: Record<string, any>, keys: string) => {
  const segments = keys.split(".");
  let value = obj;
  do {
    value = value[segments.shift()!];
  } while (typeof value === `object` && value && segments.length);
  return value;
};

const unsetRecursive = <T extends Record<string, any>>(
  obj: T,
  keys: string[],
): Partial<T> => {
  const key = keys.shift()!;
  if (keys.length === 0) {
    const { [key]: _, ...other } = obj;
    return other as any;
  }
  return {
    ...obj,
    [key]: unsetRecursive(obj[key], keys),
  };
};

/**
 * Deletes property from an object with path specified by dot notation.
 * Returns a new object, does not mutate the original.
 */
export const unset = <T extends Record<string, any>>(
  obj: T,
  keys: string,
): Partial<T> => {
  return unsetRecursive(obj, keys.split("."));
};

const leadingSlashRe = /^\/+/;
export const removeLeadingSlash = (path: string) => {
  return path.replace(leadingSlashRe, ``);
};

export const addTrailingSlash = (path: string) => {
  return path.endsWith(`/`) ? path : `${path}/`;
};

export const isScalarType = (
  value: any,
): value is boolean | number | string => {
  switch (typeof value) {
    case `object`:
    case `undefined`:
      return false;
    default:
      return true;
  }
};

/**
 * Method that appends key and value to the URLSearchParams object. The value
 * must be a scalar type otherwise it is ignored. Passing `undefined` skips the
 * value as well.
 * @param searchParams
 * @param key
 * @param value
 */
export const appendQueryParameter = (
  searchParams: URLSearchParams,
  key: string,
  value: any,
) => {
  if (isScalarType(value)) {
    searchParams.append(key, String(value));
  } else if (typeof value === `object`) {
    console.warn(
      `Property "${key}" in request message is not send in request body, and cannot be send in a query-string parameters because it contains a value of type _object_. Property ${key}" will be skipped.`,
    );
  }
};

const pathParameterRe = /{([^}]+)}/g;
/**
 * Replaces path parameters in the path with values from the request message. It also removes the consumed parameters
 * from the request message, so the object passed in is mutated and contains only the remaining fields.
 */
export const replacePathParameters = <RequestMessage>(
  path: string,
  requestMessage?: RequestMessage,
): [string, Partial<RequestMessage>] => {
  let requestMessageWOPathParams: Partial<RequestMessage> = requestMessage!;
  const pathWithParams = path.replace(pathParameterRe, (_a, c1) => {
    const parameterPath = c1.split("=", 2)[0] as string;
    // the path might contain dot notaion to nested fields
    const value = requestMessage && get(requestMessage, parameterPath);
    if (value == null) {
      throw new Error(`Missing required path parameter: ${parameterPath}`);
    }
    if (typeof value === "string") {
      if (value === "") {
        throw new Error(`Path parameter "${parameterPath}" must not be empty`);
      }
    } else if (typeof value !== "number" && typeof value !== "bigint") {
      throw new Error(
        `path parameter "${parameterPath}" must be a string, number, or bigint, received "${value}" which is "${typeof value}"`,
      );
    }
    // TODO: we can validate the value against the pattern specified in the path
    requestMessageWOPathParams = unset(
      requestMessageWOPathParams,
      parameterPath,
    );
    return String(value);
  });
  return [pathWithParams, requestMessageWOPathParams!];
};

export type RequestConfig = {
  basePath?: string;
  bearerToken?: string | (() => string);
};

// taken from http.proto, only the cusom method is not currently supported
// @see https://github.com/googleapis/googleapis/blob/5ca19108a3251b1cb8dd5b246b37ce2eed2dc92f/google/api/http.proto#L324
export type HttpMethod = `DELETE` | `GET` | `PATCH` | `POST` | `PUT`;

export class RPC<RequestMessage, ResponseMessage> {
  /**
   * HTTP method of the RPC as described by the google.api.http option
   */
  readonly method: HttpMethod;
  /**
   * URL path of the RPC as described by the google.api.http option
   */
  readonly path: string;
  /**
   * Optional: the path to body in RequestMessage, if specified by the google.api.http option
   */
  readonly bodyKey?: string;

  constructor(method: HttpMethod, path: string, bodyKey?: string) {
    this.method = method;
    this.path = path;
    this.bodyKey = bodyKey;
  }

  /**
   * Creates a JavaScript Request object which can be used directly with fetch API. If you are using other HTTP client,
   * you can read the request properties from the Request object.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Request
   * @param config the request configuration for the RPC
   * @param params the request message for the RPC as defined in the proto file
   */
  createRequest(config: RequestConfig, params: RequestMessage): Request {
    let unconsumedParams: Partial<RequestMessage> | undefined;
    let pathWithParams: string;
    [pathWithParams, unconsumedParams] = replacePathParameters(
      this.path,
      params,
    );
    // we must remove leading slash from the path and add trailing slash to the base path, otherwise only the hostname
    // part of the base path will be used :/
    const url = config.basePath
      ? new URL(
          removeLeadingSlash(pathWithParams),
          addTrailingSlash(config.basePath),
        )
      : new URL(pathWithParams, (globalThis as any).location.href);

    let body: string | undefined = undefined;
    if (params && this.method !== `DELETE` && this.method !== `GET`) {
      if (this.bodyKey) {
        body = JSON.stringify(get(params, this.bodyKey));
        unconsumedParams = unset(unconsumedParams, this.bodyKey);
      } else {
        body = JSON.stringify(params);
        unconsumedParams = undefined;
      }
    }

    if (unconsumedParams) {
      for (const [k, v] of Object.entries(unconsumedParams)) {
        if (Array.isArray(v)) {
          for (const item of v) {
            appendQueryParameter(url.searchParams, k, item);
          }
        } else {
          appendQueryParameter(url.searchParams, k, v);
        }
      }
    }

    const headers = new Headers();
    if (body) {
      headers.set("Content-Type", "application/json; charset=utf-8");
    }
    const bearerToken =
      typeof config.bearerToken === "function"
        ? config.bearerToken()
        : config.bearerToken;
    if (bearerToken) {
      headers.set("Authorization", `Bearer ${bearerToken}`);
    }

    const request = new Request(url.href, {
      body,
      method: this.method,
      headers,
    });
    return request;
  }

  /**
   * Simple identity function that just types the input as ResponseMessage.
   * Usefull for TypeScript code to assing a type to the response.
   */
  responseTypeId(response: any): ResponseMessage {
    return response as ResponseMessage;
  }
}

/**
 * Given the protobuf option google.api.resource.pattern, return a string that can be compiled into a regexp which will
 * extract the path parameters from a path as a _named_ capture group.
 * @param pathPattern must follow the protobuf option google.api.resource.pattern format
 * @example projects/{project}/documents/{document}/results/{result}
 * @returns
 */
export const pathPatternToParseRegexp = (pathPattern: string): string =>
  pathPattern.replace(/(\/)|(?:\{(\w+)\})/g, ((
    _match: string,
    forwardSlash: string,
    pathParameter: string,
  ) => {
    if (forwardSlash) {
      return `\\/`;
    }
    if (pathParameter) {
      return `(?<${pathParameter}>[^/]+)`;
    }
  }) as any);

// path-to-regexp only accepts word characters in a parameter name, but a path parameter may use the dot notation to
// address a nested field, e.g. `{flip.name}`
const toPathPatternParamName = (name: string) => name.replace(/\W/g, `_`);
// the path segment preceding a wildcard is a plural collection identifier per Google AIP, e.g. `documents/*`
const singularize = (segment: string) =>
  segment.length > 1 && segment.endsWith(`s`) ? segment.slice(0, -1) : segment;
// characters that path-to-regexp treats as syntax - most notably the colon used by AIP custom methods
const escapePathPatternLiteral = (literal: string) =>
  literal.replace(/[:\\*+?()]/g, (char) => `\\${char}`);

/**
 * Converts the `RPC.path`, which is in the `google.api.http` format, into a path pattern understood by Mock Service
 * Worker and other path-to-regexp based routers. The `{parameter}` wrappers are replaced with `:parameter`, the
 * `{name=projects/*}` patterns are expanded literally and the remaining special characters are escaped.
 * @param path the `RPC.path`
 * @param basePath optional prefix, joined the same way as in `RPC#createRequest`. Pass the `RequestConfig.basePath` to
 * match a service mounted on a sub-path, because a root-relative pattern resolves against the origin only and would
 * drop the sub-path. Pass `*` to match any origin and sub-path.
 * @example toPathPattern(`/v1/{name=projects/*}:count`) // `/v1/projects/:project\\:count`
 * @example toPathPattern(`/v1/{name=projects/*}`, `https://api.test/gateway`) // `https://api.test/gateway/v1/projects/:project`
 */
export const toPathPattern = (path: string, basePath?: string): string => {
  const usedNames = new Set<string>();
  const uniqueName = (name: string) => {
    let unique = name;
    for (let i = 2; usedNames.has(unique); i++) {
      unique = `${name}_${i}`;
    }
    usedNames.add(unique);
    return unique;
  };
  let pattern = ``;
  let lastIndex = 0;
  for (const match of path.matchAll(pathParameterRe)) {
    pattern += escapePathPatternLiteral(path.slice(lastIndex, match.index));
    lastIndex = match.index + match[0].length;
    const [parameterPath, parameterPattern] = match[1].split("=", 2);
    // the parameter name is the fallback for wildcards which are not preceded by a collection identifier
    const fallbackName = toPathPatternParamName(parameterPath);
    if (!parameterPattern) {
      pattern += `:${uniqueName(fallbackName)}`;
      continue;
    }
    let collection: string | undefined;
    pattern += parameterPattern
      .split("/")
      .map((segment) => {
        if (segment !== `*` && segment !== `**`) {
          collection = segment;
          return escapePathPatternLiteral(segment);
        }
        const name = uniqueName(
          collection
            ? toPathPatternParamName(singularize(collection))
            : fallbackName,
        );
        // `**` spans multiple segments. The custom pattern must not contain `*` nor `?`, because Mock Service Worker
        // rewrites `*` into a nested capturing group and truncates the path at the first `?`.
        return segment === `**` ? `:${name}(.+)` : `:${name}`;
      })
      .join("/");
  }
  pattern += escapePathPatternLiteral(path.slice(lastIndex));
  // the basePath is deliberately not escaped, Mock Service Worker escapes the `://` of the protocol and the `:` of the
  // port on its own and would double-escape ours
  return basePath
    ? addTrailingSlash(basePath) + removeLeadingSlash(pattern)
    : pattern;
};

const reProtoPathPattern = /{([^/]+)}/g;
/**
 * For a protobuf option google.api.resource.pattern, returns a resource name parser and compiler.
 * @param pattern must follow the protobuf option google.api.resource.pattern format
 * @example projects/{project}/documents/{document}/results/{result}
 * @returns parseCompile
 * @param parseCompile.compile a function that takes an object with the path parameters and returns a resource name
 * @param parseCompile.parse a function that takes a resource name and returns an object with the path parameters
 */
export function getNameParser<Keys extends string>(
  pattern: string,
): {
  compile: (params: { [key in Keys]: string }) => string;
  parse: (name: string) => { [key in Keys]: string };
} {
  const pathParseRegexpString = pathPatternToParseRegexp(pattern);
  const reParse = new RegExp(pathParseRegexpString);
  const compile = (params: { [key in Keys]: string }) =>
    pattern.replace(
      reProtoPathPattern,
      (_match, paramName: Keys) => params[paramName],
    );
  const parse = (name: string) => {
    const match = name.match(reParse);
    if (!match) {
      throw new Error(`Attempt to parse invalid name: "${name}"`);
    }
    const { groups } = match;
    return groups as { [key in Keys]: string };
  };
  return { compile, parse };
}

// @see https://grpc.github.io/grpc/core/md_doc_statuscodes.html
export const GrpcStatusCode = {
  OK: 0,
  CANCELLED: 1,
  UNKNOWN: 2,
  INVALID_ARGUMENT: 3,
  DEADLINE_EXCEEDED: 4,
  NOT_FOUND: 5,
  ALREADY_EXISTS: 6,
  PERMISSION_DENIED: 7,
  RESOURCE_EXHAUSTED: 8,
  FAILED_PRECONDITION: 9,
  ABORTED: 10,
  OUT_OF_RANGE: 11,
  UNIMPLEMENTED: 12,
  INTERNAL: 13,
  UNAVAILABLE: 14,
  DATA_LOSS: 15,
  UNAUTHENTICATED: 16,
} as const;
export type GrpcStatusCode =
  (typeof GrpcStatusCode)[keyof typeof GrpcStatusCode];

